/**
 * VRG-156 — NFC tap read-range for a (capability, size) selection.
 *
 * NFC read range is antenna/size-driven (not chip-driven): larger antennas tap at a
 * few cm; sub-18mm tags collapse toward touch-contact. Composite/rectangular and card
 * formats have a large enough antenna to read at ~4 cm. Verify + Trace (EM4425 dual-
 * frequency) additionally reads far-field with a UHF reader.
 *
 * TODO(VRG-156) read-range figures from tag-technology-comparison.md — confirm w/ finished-inlay test.
 */
import type { TagCapability } from './pricing'

/** Human-readable tap (and, for Trace, far-field) read range for a size selection. */
export function readRange(capability: TagCapability, size: string): string {
  const s = String(size).toLowerCase()
  const uhf = capability === 'Verify + Trace' ? ' · up to ~10 m (RFID)' : ''

  // Composite / rectangular (45×25, 70×20), card (85.6×54), or a Closure band —
  // all carry a large enough antenna for a comfortable ~4 cm tap.
  if (/x/.test(s) || s.includes('card') || s.includes('neck')) return `~4 cm (tap)${uhf}`
  // Cap-disc sits in a small closure void — mid-range.
  if (s.includes('cap')) return `~2–3 cm (tap)${uhf}`

  const mm = parseFloat(s) // '30mm' → 30, '85.6x54mm' handled above
  if (!Number.isFinite(mm)) return `~2–3 cm (tap)${uhf}`
  if (mm >= 30) return `~4 cm (tap)${uhf}`
  if (mm >= 18) return `~2–3 cm (tap)${uhf}`
  return `~1 cm (touch)${uhf}`
}

/**
 * VRG-156 — derive the customization-canvas shape (and mm dimensions where parseable)
 * from a SIZE selection string. The product-level `shape` metafield is too coarse — a
 * form that offers both round and rectangular sizes reports `'rect'` for every size, so
 * a round 25mm disc draws on a square canvas. Resolve the shape from the actually-chosen
 * size instead:
 *   - composite "W x H" (e.g. "45x25mm", "70x20mm", card ID-1 "85.6x54mm") → rectangular.
 *   - single-dimension round mm (e.g. "25mm", "30mm", "6mm", "18mm") → round disc (diameter).
 *   - descriptive / apparel / named formats (no reliable dims) → rectangular canvas.
 */
export function sizeToShape(
  size: string,
): { shape: 'round' | 'rect'; widthMm?: number; heightMm?: number } {
  const s = String(size ?? '').trim().toLowerCase()

  // Composite W x H (checked first — "45x25mm" must not be read as a round "45").
  const composite = s.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/)
  if (composite) {
    return { shape: 'rect', widthMm: parseFloat(composite[1]), heightMm: parseFloat(composite[2]) }
  }

  // Single-dimension round mm disc → diameter drives both width + height.
  const single = s.match(/^(\d+(?:\.\d+)?)\s*mm$/)
  if (single) {
    const d = parseFloat(single[1])
    return { shape: 'round', widthMm: d, heightMm: d }
  }

  // Descriptive / apparel / named formats — no reliable dims; default to a rect canvas.
  return { shape: 'rect' }
}
